import type {
  CabinetLoginRequest,
  CabinetMfaVerifyRequest,
  CabinetTenantMembership,
  PermissionCode,
} from "@planerius/contracts/cabinet-auth"
import {
  CabinetContext,
  CabinetLoginResponse,
  CabinetMfaEnrollmentResult,
} from "@planerius/contracts/cabinet-auth"
import type { z } from "zod"

import { accessTokenStore } from "./access-token"
import { VALIDATE_RESPONSES } from "./config"
import { apiRequest, forgetSession, markSessionAlive } from "./http"
import { ApiProblem } from "./problem"

/**
 * Контур входа и профиля.
 *
 * Схемами Zod здесь проверяются ровно два ответа — вход и /me. Оба маленькие, оба
 * приходят один раз за сессию, и оба решают, что человек вообще увидит. Журнал так
 * не проверяется: день на пятнадцать мастеров разбирать дорого, а незнакомое поле
 * в ответе не должно мешать работать.
 */

function parseResponse<S extends z.ZodType>(schema: S, value: unknown, where: string): z.output<S> {
  if (!VALIDATE_RESPONSES) return value as z.output<S>

  const result = schema.safeParse(value)
  if (!result.success) {
    // В разработке расхождение контракта и сервера обязано падать громко и сразу:
    // молча подстроиться под чужой ответ — значит увезти расхождение в бой.
    throw new ApiProblem({
      code: "bad_response",
      status: 200,
      title: "Ответ сервиса не соответствует контракту",
      detail: `${where}: ${result.error.message}`,
    })
  }
  return result.data
}

/** Сохранить выданную пару. refreshToken в браузере всегда null — он уходит cookie. */
function keepTokens(tokens: { accessToken: string; expiresAt: string }): void {
  accessTokenStore.write(tokens.accessToken, tokens.expiresAt)
  markSessionAlive()
}

export async function login(request: CabinetLoginRequest): Promise<CabinetLoginResponse> {
  const body = await apiRequest<unknown>("/cabinet/auth/login", {
    method: "POST",
    body: request,
    authenticated: false,
  })
  const parsed = parseResponse(CabinetLoginResponse, body, "login")
  if (parsed.status === "authenticated") keepTokens(parsed.result.tokens)
  return parsed
}

export async function verifyMfa(request: CabinetMfaVerifyRequest): Promise<CabinetLoginResponse> {
  const body = await apiRequest<unknown>("/cabinet/auth/mfa/verify", {
    method: "POST",
    body: request,
    authenticated: false,
  })
  const parsed = parseResponse(CabinetLoginResponse, body, "mfa/verify")
  if (parsed.status === "authenticated") keepTokens(parsed.result.tokens)
  return parsed
}

/**
 * Заведение второго фактора ПРИ ВХОДЕ: код сошёлся — коды показаны и человек внутри.
 *
 * ЖИВЁТ ЗДЕСЬ, А НЕ В mfa.api.ts, РАДИ ОДНОЙ СТРОКИ — keepTokens. Ответ несёт выданную
 * сессию, и без сохранения токена происходило вот что: фактор включался, сервер выдавал
 * сессию, кабинет её выбрасывал — и первый же запрос отвечал «войдите заново». Человек
 * оставался снаружи учётки, в которой только что включил обязательный второй фактор.
 * Разбор ответа по контракту нужен по той же причине, что у входа: расхождение обязано
 * падать здесь, а не превращаться в пустой экран.
 */
export async function confirmMfaEnrollment(
  enrollToken: string,
  code: string,
  deviceLabel?: string,
): Promise<{ readonly recoveryCodes: readonly string[]; readonly login: CabinetLoginResponse }> {
  const body = await apiRequest<unknown>("/cabinet/auth/mfa/enroll/confirm", {
    method: "POST",
    body: { enrollToken, code, ...(deviceLabel === undefined ? {} : { deviceLabel }) },
    authenticated: false,
  })
  const parsed = parseResponse(CabinetMfaEnrollmentResult, body, "mfa/enroll/confirm")
  if (parsed.login.status === "authenticated") keepTokens(parsed.login.result.tokens)
  return parsed
}

/** Салоны, где человек работает. Здесь же видны непринятые приглашения. */
export function listTenants(signal?: AbortSignal): Promise<CabinetTenantMembership[]> {
  return apiRequest<CabinetTenantMembership[]>("/cabinet/auth/tenants", { signal })
}

/**
 * Выбор салона. Ответ несёт НОВЫЙ access-токен: у прежнего другой салон в RLS, другая роль
 * и другие права, поэтому после выбора профиль перечитывается целиком.
 */
export async function selectTenant(tenantId: string): Promise<void> {
  const pair = await apiRequest<{ accessToken: string; expiresAt: string }>(
    "/cabinet/auth/tenant",
    { method: "POST", body: { tenantId } },
  )
  keepTokens(pair)
}

export async function fetchContext(signal?: AbortSignal): Promise<CabinetContext> {
  const body = await apiRequest<unknown>("/cabinet/auth/me", { signal })
  return parseResponse(CabinetContext, body, "auth/me")
}

/**
 * Свой профиль без выбранного салона. Нужен подписи «вы вошли как …» на экране выбора
 * салона: там контекста салона ещё нет, а имя человека показать надо.
 */
export interface CabinetMemberSelf {
  readonly userId: string
  readonly displayName: string
  readonly permissions: readonly PermissionCode[]
  readonly branchIds: readonly string[]
  readonly restrictToOwnRecords: boolean
  readonly employeeId: string | null
}

export function fetchMemberSelf(signal?: AbortSignal): Promise<CabinetMemberSelf> {
  return apiRequest<CabinetMemberSelf>("/cabinet/members/me", { signal })
}

/**
 * Выход из режима поддержки. Сервер гасит cookie — сотрудник платформы возвращается
 * к своему входу, а не к сессии салона. Поэтому после вызова кабинет уходит на экран входа.
 */
export async function endImpersonation(note?: string): Promise<void> {
  try {
    await apiRequest<void>("/cabinet/impersonation/end", {
      method: "POST",
      body: note === undefined ? {} : { note },
    })
  } finally {
    forgetSession()
  }
}

/**
 * Выход. Cookie гасит сервер; локально стирается токен из памяти вкладки.
 * Отказ сервера не отменяет локального выхода: человек нажал «выйти» и обязан выйти.
 */
export async function logout(allDevices = false): Promise<void> {
  try {
    await apiRequest<void>("/cabinet/auth/logout", { method: "POST", body: { allDevices } })
  } finally {
    forgetSession()
  }
}
